/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useDataPollerForm } from "./hooks";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { Label } from "../../../ui/label";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { ShadcnHeading } from "../../../ui/shadcn-heading";
import { useStoreDispatch } from "../../../../store/hooks";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { store } from "../../../../store";

const DataPolling: React.FC = () => {
  const dispatch = useStoreDispatch();
  const {
    dataPolling,
    setDataPolling,
    handleResetDataPollers,
    submitHandler,
  } = useDataPollerForm();

  const onFieldChange = (name: string, value: string) => {
    setDataPolling({ ...dataPolling, [name]: value });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, [name]: "" }));
  };

  const checkboxHandler = (name: string, checked: boolean) => {
    setDataPolling({ ...dataPolling, [name]: checked });
  };

  return (
    <div
      className="flex flex-col w-full overflow-y-auto px-4 md:px-4 pt-1 pb-10"
      style={{ color: "var(--foreground)" }}
    >
      
      <div
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "24px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--background)",
        }}
      >
        <form autoComplete="off">
          <div className="space-y-5 max-w-lg">
            {/* Enable */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="enable"
                checked={dataPolling?.enable}
                onCheckedChange={(checked) => checkboxHandler("enable", !!checked)}
                className="m-0"
              />
              <Label htmlFor="enable" className="cursor-pointer">
                Enable
              </Label>
            </div>

            {/* Sub-checkboxes */}
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="kpiEnable"
                  disabled={!dataPolling?.enable}
                  checked={dataPolling?.kpiEnable}
                  onCheckedChange={(checked) => checkboxHandler("kpiEnable", !!checked)}
                  className="m-0"
                />
                <Label htmlFor="kpiEnable" className="cursor-pointer">
                  Kpi data
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="dbgEnable"
                  disabled={!dataPolling?.enable}
                  checked={dataPolling?.dbgEnable}
                  onCheckedChange={(checked) => checkboxHandler("dbgEnable", !!checked)}
                  className="m-0"
                />
                <Label htmlFor="dbgEnable" className="cursor-pointer">
                  Debug logs
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="ebEnable"
                  disabled={!dataPolling?.enable}
                  checked={dataPolling?.ebEnable}
                  onCheckedChange={(checked) => checkboxHandler("ebEnable", !!checked)}
                  className="m-0"
                />
                <Label htmlFor="ebEnable" className="cursor-pointer">
                  Error bundles
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="cmEnable"
                  disabled={!dataPolling?.enable}
                  checked={dataPolling?.cmEnable}
                  onCheckedChange={(checked) => checkboxHandler("cmEnable", !!checked)}
                  className="m-0"
                />
                <Label htmlFor="cmEnable" className="cursor-pointer">
                  Cm data
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="alarmEnable"
                  disabled={!dataPolling?.enable}
                  checked={dataPolling?.alarmEnable}
                  onCheckedChange={(checked) => checkboxHandler("alarmEnable", !!checked)}
                  className="m-0"
                />
                <Label htmlFor="alarmEnable" className="cursor-pointer">
                  Polling alarms
                </Label>
              </div>
            </div>

            {/* Host */}
            <InputField
              name="host"
              label="Host"
              disabled={!dataPolling?.enable}
              value={dataPolling?.host ?? ""}
              onChange={onFieldChange}
              placeholder="Enter host"
            />

            {/* Port */}
            <InputField
              name="port"
              label="Port"
              type="number"
              required
              disabled={!dataPolling?.enable}
              value={dataPolling?.port ?? ""}
              onChange={onFieldChange}
              min={1}
              max={65535}
              placeholder="Enter port"
              tooltipText="Value should be between 1 and 65535"
            />

            {/* Pubkey */}
            <InputField
              name="pubkey"
              label="Pubkey"
              disabled={!dataPolling?.enable}
              value={dataPolling?.pubkey ?? ""}
              onChange={onFieldChange}
              placeholder="Enter pubkey"
            />

            {/* Max days */}
            <InputField
              name="maxDays"
              label="Max days"
              type="number"
              required
              disabled={!dataPolling?.enable}
              value={dataPolling?.maxDays ?? ""}
              onChange={onFieldChange}
              min={3}
              max={65535}
              placeholder="Enter max days"
              tooltipText="Value should be between 3 and 65535"
            />

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" variant="outline" onClick={handleResetDataPollers}>
                Reset
              </Button>
              <Button type="button" onClick={submitHandler}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>

    </div>
  );
};

export default DataPolling;

